import React from 'react';
import { Spinner } from '@oysterjs/ui/spinner';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import {
  getApplicationError,
  getValidationErrors,
  messageFromError
} from '@oysterjs/core/graphql/errors';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import {
  Action,
  MailboxAccountStatus,
  Resource,
  Role,
  SubscriptionEntitlement,
  useGetAgencyUserQuery,
  useGetTriggerEmailBuilderOptionsQuery,
  useGetTriggerEmailFoldersQuery,
  useGetCustomPresetsQuery,
  type CreateTriggerDefinitionInput,
  type CustomPresetFieldsFragment,
  type TriggerDefinitionFieldsFragment
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { triggerHref } from '~/pages/workflows/trigger-utils';
import { useEntitlements } from '~/components/entitlements';
import { useCurrentAgencyUser } from '~/components/current-user';
import { MailboxUpgradeNotice } from '~/components/mailbox/mailbox-guard';
import { createTriggerDefinition, updateTriggerDefinition } from '~/api/data/triggers/mutations';
import {
  emailConfigFromResponse,
  isEmailVersionConflict,
  type EmailConfigDraft,
  type EmailErrors,
  type EmailOptions
} from '~/api/data/triggers/email-config';
import {
  EmailBuilderForm,
  type EmailCreationDraft,
  type EmailSaveOutcome
} from '~/components/triggers/email-builder/email-trigger-builder';
import { type FolderCatalog } from '~/components/triggers/email-builder/creation-when';
import {
  CreationEventContext,
  CreationEventLoadState
} from '~/components/triggers/email-builder/creation-event';

const LOAD_FAILED =
  'Mailbox options could not be loaded. Agency trigger-monitoring access is required. Your existing draft is preserved.';
const OPTIONS_UNAVAILABLE =
  'Mailbox or preset options are unavailable. Reload options; your draft is preserved.';
// The server only lets agency admins configure mailbox triggers, so a member's
// refusal is final: say why in plain words instead of offering a retry.
const ADMIN_ONLY =
  'Only agency admins can set up mailbox triggers. Choose another event, or ask an admin to create this one.';
const SAVE_UNKNOWN =
  'The save result could not be confirmed. Check the trigger list before trying again; the save will not be replayed automatically.';

const FolderLoader = ({
  accountId,
  onChange,
  onReadDenied
}: {
  accountId: string;
  onChange: (id: string, value: FolderCatalog) => void;
  onReadDenied: () => void;
}) => {
  const query = useGetTriggerEmailFoldersQuery({
    variables: { accountId },
    fetchPolicy: 'network-only',
    errorPolicy: 'none'
  });
  React.useEffect(() => {
    if (getApplicationError(query.error)?.code === ApplicationErrorCode.Forbidden) onReadDenied();
    onChange(accountId, {
      data: query.data?.triggerEmailFolders,
      loading: query.loading,
      error: query.error
        ? messageFromError(query.error, 'Folder options could not be loaded.')
        : !query.loading && query.data?.triggerEmailFolders == null
          ? 'Folder options are unavailable. Reload this mailbox’s folders.'
          : undefined,
      retry: () => {
        void query.refetch().catch(() => {});
      }
    });
  }, [accountId, onChange, onReadDenied, query.data, query.error, query.loading, query.refetch]);
  return null;
};

// A mailbox trigger needs a connected mailbox to watch, so without one the editor
// would only be a dead end: lead to Settings instead. Other events stay selectable.
const NoConnectedMailbox = ({ reconnect }: { reconnect: boolean }) => (
  <CreationEventLoadState>
    <LibraryListEmptyState
      embedded
      icon={EnvelopeIcon}
      title={reconnect ? 'Reconnect a mailbox to continue' : 'Connect a mailbox to get started'}
      description={
        reconnect
          ? 'Cara has lost access to every connected mailbox. Each one has to be reconnected by its owner in Settings before a trigger can watch it.'
          : 'Mailbox triggers watch Outlook mailboxes connected to Cara. Connect one in Settings, then come back to choose the mailboxes and folders this trigger watches.'
      }
      action={
        <Button color="sky" href="/app/settings/mailbox">
          {reconnect ? 'Open mailbox settings' : 'Connect a mailbox'}
        </Button>
      }
    />
  </CreationEventLoadState>
);

const useMailboxCreationOptions = (skip = false, definitionId?: string) => {
  const optionsQuery = useGetTriggerEmailBuilderOptionsQuery({
    skip,
    variables: { definitionId },
    fetchPolicy: 'network-only',
    errorPolicy: 'none'
  });
  const liveOptions = optionsQuery.data?.triggerEmailBuilderOptions;
  const [cachedOptions, setCachedOptions] = React.useState<EmailOptions>();
  const [cachedDefinitionId, setCachedDefinitionId] = React.useState(definitionId);
  const [cachedPresets, setCachedPresets] = React.useState<CustomPresetFieldsFragment[] | null>(
    null
  );
  const options = liveOptions ?? (cachedDefinitionId === definitionId ? cachedOptions : undefined);
  const needsPresets = !!options?.presets.some((preset) => preset.ready);
  const denied = getApplicationError(optionsQuery.error)?.code === ApplicationErrorCode.Forbidden;
  const presetsQuery = useGetCustomPresetsQuery({
    skip: skip || denied || !needsPresets,
    fetchPolicy: 'network-only',
    errorPolicy: 'none'
  });
  React.useEffect(() => {
    if (liveOptions && !optionsQuery.error) {
      setCachedOptions(liveOptions);
      setCachedDefinitionId(definitionId);
    }
  }, [liveOptions, optionsQuery.error, definitionId]);
  React.useEffect(() => {
    if (presetsQuery.data && !presetsQuery.error) setCachedPresets(presetsQuery.data.customPresets);
  }, [presetsQuery.data, presetsQuery.error]);
  return { optionsQuery, presetsQuery, liveOptions, options, cachedPresets, needsPresets };
};

// The all-events creation page loads the mailbox options once and shares them, so
// switching to another event and back shows the editor at once instead of
// unmounting the query and flashing a loading card while it refetches.
const MailboxCreationOptionsContext = React.createContext<ReturnType<
  typeof useMailboxCreationOptions
> | null>(null);
export const MailboxCreationOptionsProvider = ({ children }: React.PropsWithChildren) => {
  const user = useCurrentAgencyUser();
  const { hasEntitlement } = useEntitlements();
  const allowed =
    !!user.agency?.id &&
    user.roles.includes(Role.Admin) &&
    hasEntitlement(SubscriptionEntitlement.Mailboxes);
  const options = useMailboxCreationOptions(!allowed);
  return (
    <MailboxCreationOptionsContext.Provider value={options}>
      {children}
    </MailboxCreationOptionsContext.Provider>
  );
};

// Maps a rejected save onto the editor: field errors by config path, plus a message.
// No error at all means the outcome is unknown, so the save is never replayed.
const saveOutcome = (
  error: Parameters<typeof getValidationErrors>[0],
  editing = false
): EmailSaveOutcome => {
  const applicationError = getApplicationError(error);
  const validation = getValidationErrors(error);
  const errors: EmailErrors = {};
  validation?.forEach((item) => {
    const path = item.field
      .join('.')
      .replace(/^input\./, '')
      .replace(/^(?:eventMatcher\.)?emailConfig\.?/, '');
    errors[path || 'config'] = item.message;
  });
  if (applicationError)
    return {
      errors,
      message: messageFromError(error, 'The server rejected this draft. It has been preserved.')
    };
  // Field errors are shown on their controls and listed under the banner, so the banner
  // points at them instead of repeating the first one.
  if (validation?.length)
    return { errors, message: 'Check the highlighted settings. Your draft has not been saved.' };
  // Only the rejected mutation response proves this draft was not written.
  // Transport and awaited refetch failures throw and retain the form's unknown-outcome lock.
  if (
    editing &&
    !error?.networkError &&
    error?.graphQLErrors.length &&
    error.graphQLErrors.every(({ message }) => isEmailVersionConflict(message))
  )
    return {
      message:
        'Someone changed this trigger. Your draft has not been saved and is still editable. Copy any changes you want to keep, then reload this page to review the latest version and reconcile your changes before saving again.'
    };
  return { uncertain: true, message: SAVE_UNKNOWN };
};

// The saved trigger in the editor's draft shape, so Edit reopens exactly what was saved.
const draftFromTrigger = (trigger: TriggerDefinitionFieldsFragment): EmailCreationDraft | null => {
  const config = trigger.eventMatcher.emailConfig;
  if (!config) return null;
  const { type, presetId, preset, useCase, prompt } = trigger.target;
  return {
    name: trigger.name,
    config: emailConfigFromResponse(config),
    target: {
      type,
      ...(presetId ? { presetId } : {}),
      ...(preset ? { preset, useCase: useCase ?? undefined } : {}),
      prompt: prompt ?? ''
    }
  };
};

type TriggerEmailCreateProps = {
  /** A saved mailbox trigger to edit; omitted when creating one. */
  existing?: TriggerDefinitionFieldsFragment;
};

// A Forbidden read is not necessarily a plan refusal. Refresh trusted identity
// before choosing upgrade vs access denied; never classify provider message text.
export const TriggerEmailCreatePage = (props: TriggerEmailCreateProps = {}) => {
  const user = useCurrentAgencyUser();
  const { hasEntitlement } = useEntitlements();
  const shared = React.useContext(MailboxCreationOptionsContext);
  const [denied, setDenied] = React.useState(false);
  const onReadDenied = React.useCallback(() => setDenied(true), []);
  const identity = useGetAgencyUserQuery({
    skip: !denied,
    fetchPolicy: 'network-only',
    errorPolicy: 'none'
  });
  const current = denied ? identity.data?.agencyUser : user;
  const admin = !!current?.agency?.id && !!current.roles.includes(Role.Admin);
  const entitled = denied
    ? !!current?.agency?.entitlements.includes(SubscriptionEntitlement.Mailboxes)
    : hasEntitlement(SubscriptionEntitlement.Mailboxes);
  const active = admin && entitled && !denied;
  // Retain an already-mounted form and its dirty-event guard across downgrade.
  // It stays hidden and disabled; setup hooks stop and saves never replay.
  const [mounted, setMounted] = React.useState(active);
  React.useEffect(() => {
    if (active) setMounted(true);
  }, [active]);
  const checking = (!denied && !user.agency?.id) || (denied && identity.loading);
  return (
    <>
      {!active && (
        <CreationEventLoadState>
          {checking ? (
            <LibraryListLoading embedded title="Checking mailbox setup access…" />
          ) : admin && !entitled && !identity.error ? (
            <MailboxUpgradeNotice />
          ) : (
            <div className="py-8" role="alert">
              <p>
                {identity.error
                  ? LOAD_FAILED
                  : admin
                    ? 'Mailbox setup access was refused. Your draft is preserved.'
                    : ADMIN_ONLY}
              </p>
              {denied && (
                <Button
                  type="button"
                  color="white"
                  onClick={() => {
                    setDenied(false);
                    void shared?.optionsQuery.refetch().catch(() => {});
                  }}
                >
                  Retry loading
                </Button>
              )}
            </div>
          )}
        </CreationEventLoadState>
      )}
      {(mounted || active) && (
        <div hidden={!active}>
          <TriggerEmailEditor
            key={props.existing?.id ?? 'create'}
            {...props}
            active={active}
            onReadDenied={onReadDenied}
          />
        </div>
      )}
    </>
  );
};

const TriggerEmailEditor = ({
  existing: loadedExisting,
  active,
  onReadDenied
}: TriggerEmailCreateProps & {
  active: boolean;
  onReadDenied: () => void;
}) => {
  const creationEvent = React.useContext(CreationEventContext);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { hasEntitlement } = useEntitlements();
  // A cache refresh cannot rebase the form's preserved draft onto a newer version.
  // Reloading the page (or opening a different definition) starts a fresh editor.
  const [existing] = React.useState(loadedExisting);
  const editing = !!existing;
  const initial = React.useMemo(() => (existing ? draftFromTrigger(existing) : null), [existing]);
  const canCreate = hasPermission(
    Resource.TriggerDefinition,
    editing ? Action.Update : Action.Create
  );
  const creationOptions = React.useContext(MailboxCreationOptionsContext);
  // A creation snapshot belongs to the caller, not this saved trigger's creator.
  const shared = editing ? null : creationOptions;
  const own = useMailboxCreationOptions(!!shared || !active, existing?.id);
  const { optionsQuery, presetsQuery, liveOptions, options, cachedPresets, needsPresets } =
    shared ?? own;
  const retryOptions = React.useCallback(() => {
    void optionsQuery.refetch().catch(() => {});
    if (needsPresets) void presetsQuery.refetch().catch(() => {});
  }, [optionsQuery.refetch, presetsQuery.refetch, needsPresets]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [folders, setFolders] = React.useState<Record<string, FolderCatalog>>({});
  const onConfigChange = React.useCallback((config: EmailConfigDraft) => {
    const ids = [...new Set((config.scope.accounts ?? []).map((account) => account.accountId))];
    setSelected((current) => (current.join('\0') === ids.join('\0') ? current : ids));
    setFolders((current) =>
      Object.fromEntries(Object.entries(current).filter(([id]) => ids.includes(id)))
    );
  }, []);
  const onFolderChange = React.useCallback((id: string, catalog: FolderCatalog) => {
    setFolders((current) => ({ ...current, [id]: catalog }));
  }, []);
  const save = async (input: CreateTriggerDefinitionInput): Promise<EmailSaveOutcome> => {
    if (!active)
      return { message: 'Mailbox setup access is unavailable. Your draft is preserved.' };
    if (existing) {
      // The saved version guards against overwriting someone else's newer save.
      const result = await updateTriggerDefinition({
        id: existing.id,
        version: existing.version,
        name: input.name,
        eventMatcher: input.eventMatcher,
        target: input.target
      });
      if (result.data?.updateTriggerDefinition?.id) {
        navigate(triggerHref(existing.id), { replace: true });
        return {};
      }
      return saveOutcome(result.error, true);
    }
    const result = await createTriggerDefinition(input);
    // A returned identity is a completed mutation, even if a follow-up list field fails.
    if (result.data?.createTriggerDefinition?.id) {
      navigate('/app/workflows/triggers', { replace: true });
      return {};
    }
    return saveOutcome(result.error);
  };
  React.useEffect(() => {
    if (getApplicationError(optionsQuery.error)?.code === ApplicationErrorCode.Forbidden)
      onReadDenied();
  }, [onReadDenied, optionsQuery.error]);
  const error = optionsQuery.error ?? (needsPresets ? presetsQuery.error : undefined);
  const loading = optionsQuery.loading || (needsPresets && presetsQuery.loading);
  const ready = !!liveOptions && (!needsPresets || !!presetsQuery.data);
  const unavailable = !loading && !error && !ready;
  const renderable = !!options && (!needsPresets || !!presetsQuery.data || cachedPresets !== null);
  if (
    !renderable &&
    getApplicationError(optionsQuery.error)?.code === ApplicationErrorCode.Forbidden
  )
    return (
      <CreationEventLoadState>
        <div className="py-8">
          <p role="alert">{ADMIN_ONLY}</p>
        </div>
      </CreationEventLoadState>
    );
  if (!renderable)
    return (
      <CreationEventLoadState>
        <div className="py-8">
          {error || unavailable ? (
            <div role="alert">
              <p>{error ? messageFromError(error, LOAD_FAILED) : OPTIONS_UNAVAILABLE}</p>
              <Button type="button" color="white" onClick={retryOptions}>
                Retry loading
              </Button>
            </div>
          ) : (
            <LibraryListLoading embedded title="Loading mailbox and preset options…" />
          )}
        </div>
      </CreationEventLoadState>
    );
  if (!options)
    return (
      <p role="alert">Mailbox options are unavailable. Return to the trigger list and reload.</p>
    );
  if (editing && !initial)
    return <p role="alert">This trigger cannot be edited here. Return to the trigger list.</p>;
  if (
    !editing &&
    canCreate &&
    !options.accounts.some((account) => account.status === MailboxAccountStatus.Connected)
  )
    return <NoConnectedMailbox reconnect={options.accounts.length > 0} />;
  return (
    <>
      {creationEvent && (!ready || !canCreate) && (
        <CreationEventLoadState>
          <p>
            Choose another event while mailbox creation is unavailable. Your current draft stays
            protected.
          </p>
        </CreationEventLoadState>
      )}
      {(error || unavailable) && (
        <div role="alert" className="mb-4 text-red-600 dark:text-red-300">
          <p>{error ? messageFromError(error, LOAD_FAILED) : OPTIONS_UNAVAILABLE}</p>
          <Button type="button" color="white" onClick={retryOptions}>
            Reload options
          </Button>
        </div>
      )}
      {loading && (
        <div
          role="status"
          className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
        >
          <Spinner size="size-4" color="neutral" />
          Refreshing options… Saving waits until they are ready.
        </div>
      )}
      {active &&
        selected.map((id) => (
          <FolderLoader
            key={id}
            accountId={id}
            onChange={onFolderChange}
            onReadDenied={onReadDenied}
          />
        ))}
      <EmailBuilderForm
        options={options}
        folders={folders}
        onConfigChange={onConfigChange}
        onSave={save}
        customPresets={presetsQuery.data?.customPresets ?? cachedPresets ?? []}
        hasProAccess={hasEntitlement(SubscriptionEntitlement.CopilotProPresets)}
        canCreate={active && canCreate}
        optionsReady={active && ready && !loading && !error}
        initial={initial ?? undefined}
        mode={editing ? 'edit' : 'create'}
      />
    </>
  );
};
